import { useId } from '@/components';
import { useProcessTimeSeriesDataset, useStablePanelSnapshot, useVisualSettings } from './hooks';
import { TimeSeriesChartPanelLoaded } from './TimeSeriesChartPanelLoaded';
import { TimeSeriesChartProps } from './time-series-chart-model';

export type TimeSeriesChartPanelProcessingProps = TimeSeriesChartProps & {
  panelKey?: string;
};

/**
 * Coordinates dataset processing and render stabilization for the time series chart panel.
 * Produces a stable snapshot of the dataset response and x-axis bounds during refetch
 * to prevent mixed-state flashes (e.g. new time range with old data).
 */
export function TimeSeriesChartPanelProcessing(props: TimeSeriesChartPanelProcessingProps) {
  const { panelKey, enableStackSort, disableDeclutterCommonLabels, xAxisTimeRange } = props;

  let chartId = useId('time-series-panel');
  if (panelKey !== undefined) {
    chartId = panelKey;
  }

  const visual = useVisualSettings();

  const liveDatasetResponse = useProcessTimeSeriesDataset({
    chartId,
    visual,
    enableStackSort,
    getGroupKey: props.groupByProps?.getGroupKey,
    disableDeclutterCommonLabels,
  });

  // Hold the previous dataset + x-axis bounds while a refetch is in-flight so the
  // chart does not render a mixed state (e.g. new time range with stale data).
  const { datasetResponse, xAxisBounds } = useStablePanelSnapshot({
    datasetResponse: liveDatasetResponse,
    xAxisTimeRange,
  });

  return <TimeSeriesChartPanelLoaded {...props} datasetResponse={datasetResponse} xAxisBounds={xAxisBounds} />;
}
